import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Chip, CircularProgress, Tooltip } from '@mui/material';
import { bool, node, oneOf, string } from 'prop-types';
import React from 'react';

const CHIP_COLORS = ['default', 'primary', 'secondary', 'success', 'error', 'info', 'warning'];

const StatusChipLabel = ({ color, label, loading = false, dot = true, icon = null }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      minWidth: 0,
      maxWidth: '100%',
    }}
  >
    {loading ? (
      <CircularProgress
        size={10}
        thickness={6}
        sx={theme => ({
          flexShrink: 0,
          color: theme.palette[color]?.main || theme.palette.text.secondary,
        })}
      />
    ) : null}
    {!loading && icon ? (
      <Box component="span" sx={{ display: 'flex', flexShrink: 0, lineHeight: 0 }}>
        {icon}
      </Box>
    ) : null}
    {!loading && !icon && dot ? (
      <Box
        component="span"
        sx={theme => ({
          width: 6,
          height: 6,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: theme.palette[color]?.main || theme.palette.text.disabled,
        })}
      />
    ) : null}
    <Box
      component="span"
      sx={{
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  </Box>
);

StatusChipLabel.propTypes = {
  color: oneOf(CHIP_COLORS).isRequired,
  label: node.isRequired,
  loading: bool,
  dot: bool,
  icon: node,
};

/**
 * Soft status chip (dot / spinner / optional icon + label). Uses Material `variant="soft"`.
 * For `@mui/joy` surfaces (Whistleblower portal), use Joy `<Chip variant="soft" />` instead.
 */
const StatusChip = ({
  label,
  color = 'default',
  loading = false,
  dot = true,
  icon = null,
  successIcon = false,
  tooltip = '',
  size = 'small',
  cellWrap = true,
  sx = null,
  className = '',
  ...chipProps
}) => {
  const resolvedIcon =
    icon || (successIcon ? <CheckCircleIcon sx={{ fontSize: 12, display: 'block' }} /> : null);

  const chip = (
    <Chip
      variant="soft"
      color={color}
      size={size}
      label={
        <StatusChipLabel
          color={color}
          label={label}
          loading={loading}
          dot={dot && !resolvedIcon}
          icon={resolvedIcon}
        />
      }
      className={className}
      sx={{ alignSelf: 'flex-start', maxWidth: '100%', ...sx }}
      {...chipProps}
    />
  );

  const wrapped = cellWrap ? (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        justifyContent: 'flex-start',
        overflow: 'hidden',
      }}
    >
      {chip}
    </Box>
  ) : (
    chip
  );

  if (!tooltip) {
    return wrapped;
  }

  return (
    <Tooltip title={tooltip}>
      <Box component="span" sx={{ display: 'inline-flex', maxWidth: '100%' }}>
        {wrapped}
      </Box>
    </Tooltip>
  );
};

StatusChip.propTypes = {
  label: node.isRequired,
  color: oneOf(CHIP_COLORS),
  loading: bool,
  dot: bool,
  icon: node,
  successIcon: bool,
  tooltip: string,
  size: oneOf(['small', 'medium']),
  cellWrap: bool,
  sx: node,
  className: string,
};

export default StatusChip;
